import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    IconButton,
    DialogTitle,
    useMediaQuery, 
    useTheme
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { url } from "../api/axios_client";
import axios from "../api/axios_client";
import { toast } from "react-toastify";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmationDialog from './AdminDashboard/DeleteConfirmationDialog';
import "../pages/BasePage/styles.css";
import { useTranslation } from 'react-i18next';
const ReportsListDialog = ({ open, onClose }) => {
    const [currentTab, setCurrentTab] = useState(0);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const tabs = [['Monitoring',`${t('Reports.monitoring')}`], ['Prediction',`${t('Reports.prediction')}`], ['Land Degradation',`${t('Reports.land_degradation')}`]];
    const countries = [
        "Algeria", "Bahrain", "Comoros", "Djibouti", "Egypt", "Iraq", "Jordan",
        "Kuwait", "Lebanon", "Libya", "Mauritania", "Morocco", "Oman", "Palestine",
        "Qatar", "Saudi Arabia", "Somalia", "Sudan", "Syria", "Tunisia",
        "United Arab Emirates", "Yemen"
    ];

    const handleDelete = (e, report) => {
        e.stopPropagation();
        setReportToDelete(report);
        setDeleteDialogOpen(true);
      };

      const confirmDelete = async () => {
        try {
          const response = await axios.delete(`${url}/reports/${reportToDelete.id}`, {
            headers: {
              "Authorization": `Bearer ${JSON.parse(localStorage.getItem("user")).jwt}`
            }
          });
          
          if (response.status === 200) {
            toast.success(t("Reports.messages.reportDeleted"));
            setReports(reports.filter(report => report.id !== reportToDelete.id));
          }
        } catch (error) {
          console.error('Error deleting report:', error);
          toast.error(t("Reports.messages.deleteError"));
        } finally {
          setDeleteDialogOpen(false);
          setReportToDelete(null);
        }
      };

    const fetchReports = async (type) => {
        if (!selectedCountry) {
            toast.error(t("Reports.messages.selectCountryFirst"));
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${url}/reports`,  {
                params: {
                    source: type.toLowerCase(),
                country: selectedCountry
                }
            });

            if (response.status !== 200) throw new Error('Network response was not ok');
            const data = await response.data;

            // Transform the string array into array of objects
            const formattedReports = data.map(report => ({
                id: report.id,
                name: report.name,
                date: new Date().toISOString() // Using current date as default
            }));

            setReports(formattedReports);
        } catch (error) {
            toast.error(t("Reports.messages.fetchReportsError"));
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (report) => {
        console.log("report", report)
        if (!selectedCountry) return;

        setDownloadingReport(report.name);
        try {
            const response = await axios.get(`${url}/reports/${report.id}`);

            if (response.status !== 200) throw new Error('Download failed');

            const jsonResponse = await response.data;

            const downloadUrl = jsonResponse.url;
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = report.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            toast.success(t("Reports.messages.reportDownloaded"));
        } catch (error) {
            toast.error(t("Reports.messages.downloadError"));
            console.error(error);
        } finally {
            setDownloadingReport(null);
        }
    };

    useEffect(() => {
        if (open && selectedCountry) {
            const tabType = tabs[currentTab][0] === 'Land Degradation' ? 'landdegradation' : tabs[currentTab][0].toLowerCase();
            fetchReports(tabType);
        }
    }, [currentTab, selectedCountry, open]);

    const filteredReports = reports ? reports.filter(report =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) : [];
    const currentUser = JSON.parse(localStorage.getItem("user"));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    height: isMobile ? "100vh" : "90vh", 
                    maxHeight: isMobile ? "100vh" : "90vh",
                },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2 }}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 10,
                        top: 16,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ width: '100%', mb: 3, mt:3 }}>
                    <Tabs
                        value={currentTab}
                        onChange={(e, newValue) => setCurrentTab(newValue)}
                        centered
                        scrollButtons="auto" 
                        variant="scrollable"
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            '& .MuiTabs-flexContainer': {
                            justifyContent: { xs: 'flex-start', sm: 'center' }, 
                        }, 
                        }}
                    >
                        {tabs.map((tab) => (
                            <Tab key={tab[0]} label={tab[1]} />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{t('Reports.select_country')}</InputLabel>
                        <Select
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            label="Select Country"
                        >
                            {countries.map((country) => (
                                <MenuItem key={country} value={country}>
                                    {t(`countries.${country}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={t('Reports.search_reports')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box sx={{ mt: 3, height: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress />
                        </Box>
                    ) : filteredReports.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography color="text.secondary">
                                {searchTerm ? `${t('Reports.no_reports_found_matching_your_search')}` : `${t('Reports.no_reports_available')}`}
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {filteredReports.map((report) => (
                                <Grid item xs={12} md={6} lg={4} xl={3} key={report.name} sx={{ my: 1 }} >
                                    <Card className="report-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: {
                                    xs: '1px solid #ccc',
                                    sm: 'none'            
                                    } }}>
                                        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box display="flex" alignItems="flex-start" mb={2}>
                                                <PictureAsPdfIcon color="error" sx={{ mr: 1.5, flexShrink: 0, mt: 0.5 }} />
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontSize: '1rem',
                                                        lineHeight: 1.3,
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {report.name}
                                                </Typography>
                                                {(currentUser?.country === selectedCountry || Number(currentUser?.role) === 3 )&& (
      <Button
        onClick={(e) => handleDelete(e, report)}
        size="small"
        color="error"
        sx={{ ml: 1 }}
      >
        <DeleteIcon />
      </Button>
    )}
                                            </Box>
                                            {/* Remove date display since it's not provided by the API */}
                                            <Box mt="auto">
                                                <Button
                                                    variant="contained"
                                                    startIcon={downloadingReport === report.name ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                                    onClick={() => handleDownload(report)}
                                                    disabled={downloadingReport === report.name}
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiButton-startIcon': {
                                                            mx:1
                                                        }
                                                    }}
                                                >
                                                    {downloadingReport === report.name ? t('Reports.downloading') : t('Reports.download')}
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
                <DeleteConfirmationDialog
  isOpen={deleteDialogOpen}
  onClose={() => setDeleteDialogOpen(false)}
  onConfirm={confirmDelete}
  itemName={reportToDelete?.name || 'this report'}
/>
            </DialogContent>
        </Dialog>
    );
};

export default ReportsListDialog;